/**
 * The resources the rules are argued against.
 *
 * Test-only. Nothing in the app imports this file, and nothing here is
 * generated: every fixture is a hand-written resource that names a specific
 * situation a modder actually gets into, so a test can say "this is the
 * stock-modkit case" rather than assembling twelve lines of XML inline and
 * leaving the reader to work out what is supposed to be wrong with it.
 *
 * The shape of the set matters as much as the contents. `cleanVehicleResource`
 * is the baseline and it must stay clean: every other fixture is that baseline
 * with exactly one thing changed, so a test failure points at the one thing.
 * When a new rule starts firing on the clean fixture, the rule is wrong — that
 * is what the baseline is for.
 *
 * Some of what is here has no test in this directory, and that is deliberate.
 * `Research/Claude/server-tools/README.md` names the fixtures that more than one
 * brief needs and says to build each of them once; the last section of this file
 * is those, and every one of them names the briefs it serves so a fixture with
 * no caller yet reads as pending rather than as dead. They live here rather than
 * in whichever test file needed one first because a test file is not an
 * importable module: importing it pulls its whole suite into the importer and
 * runs every one of its cases a second time.
 */

import {
  CLEAN_MANIFEST,
  COLLISION_ASSET,
  STREAM_ONLY_MANIFEST,
  vehiclesMeta,
  type PackFiles,
} from "./demoPacks";
import type { ResourceDrop } from "./intake";
import type { GtaFile, GtaResourceInput } from "./types";

// ─── Moved into production, re-exported from here ────────────────────────────

/**
 * The packs a tool can load as a demo now live in `demoPacks.ts`.
 *
 * They are the only fixtures the app itself uses: the four server-owner free
 * tools offer them in their empty state, fed through the real intake path, so a
 * new user with no broken `resources/` folder still sees real findings. A
 * fixture with a production caller cannot live in a file the app must not
 * import, and a *copy* in each place would be two descriptions of one pack where
 * only one of them has a test.
 *
 * Re-exported rather than relocated in every caller because twelve test files
 * import these names from here; the dependency runs prod ← test, which is the
 * direction that stays safe. Nothing else about this file changed, and the
 * header above still holds: the app imports `demoPacks.ts`, never this.
 */
export {
  CLEAN_MANIFEST,
  COLLISION_ASSET,
  EXISTING_SERVER_CFG,
  STREAM_ONLY_MANIFEST,
  packNoManifest,
  twoPacksSameLayout,
  vehiclesMeta,
} from "./demoPacks";
export type { PackFiles } from "./demoPacks";

// ─── Building blocks ─────────────────────────────────────────────────────────

/** One file in a fixture. Size only matters where a rule compares sizes. */
export function file(path: string, size = 1024): GtaFile {
  const name = path.split("/").pop() ?? path;
  const dot = name.lastIndexOf(".");
  return {
    path,
    name,
    extension: dot <= 0 ? "" : name.slice(dot + 1).toLowerCase(),
    size,
  };
}

/** A resource input from a list of paths and a map of file texts. */
export function resource(
  name: string,
  files: GtaFile[],
  texts: Record<string, string>,
): GtaResourceInput {
  return {
    name,
    files,
    texts: new Map(Object.entries(texts).map(([path, text]) => [path.toLowerCase(), text])),
  };
}

// ─── Manifests ───────────────────────────────────────────────────────────────

/**
 * The manifest form that gets reported as broken by every regex-only checker:
 * two library includes that are real paths in someone else's resource.
 */
export const RESOURCE_INCLUDE_MANIFEST = `fx_version 'cerulean'
game 'gta5'

shared_scripts {
  '@ox_lib/init.lua',
}

server_scripts {
  '@oxmysql/lib/MySQL.lua',
  'server/main.lua',
}

dependencies {
  'ox_lib',
  'oxmysql',
  '/server:6683',
  '/gameBuild:3095',
}
`;

/** Works on Windows, 404s on the server. */
export const CASE_MISMATCH_MANIFEST = `fx_version 'cerulean'
game 'gta5'

files {
  'Data/CarCols.meta',
}

data_file 'CARCOLS_FILE' 'Data/CarCols.meta'
`;

// ─── Vehicle metadata ────────────────────────────────────────────────────────

export const CLEAN_VEHICLES = `<?xml version="1.0" encoding="UTF-8"?>
<CVehicleModelInfo__InitDataList>
  <residentTxd>vehshare</residentTxd>
  <InitDatas>
    <Item>
      <modelName>wsatlas</modelName>
      <txdName>wsatlas</txdName>
      <handlingId>wsatlas</handlingId>
      <gameName>WSATLAS</gameName>
      <vehicleMakeName>WORKSHOP</vehicleMakeName>
      <audioNameHash>sultan</audioNameHash>
      <layout>LAYOUT_STANDARD</layout>
      <flags>FLAG_HAS_LIVERY</flags>
      <vehicleClass>VC_SEDAN</vehicleClass>
    </Item>
  </InitDatas>
</CVehicleModelInfo__InitDataList>`;

export const CLEAN_HANDLING = `<?xml version="1.0" encoding="UTF-8"?>
<CHandlingDataMgr>
  <HandlingData>
    <Item type="CHandlingData">
      <handlingName>wsatlas</handlingName>
    </Item>
  </HandlingData>
</CHandlingDataMgr>`;

export const CLEAN_CARVARIATIONS = `<?xml version="1.0" encoding="UTF-8"?>
<CVehicleModelInfoVariation>
  <variationData>
    <Item>
      <modelName>wsatlas</modelName>
      <kits>
        <Item>65534_wsatlas_modkit</Item>
      </kits>
      <sirenSettings value="0" />
      <lightSettings value="0" />
    </Item>
  </variationData>
</CVehicleModelInfoVariation>`;

export const CLEAN_CARCOLS = `<?xml version="1.0" encoding="UTF-8"?>
<CVehicleModelInfoVarGlobal>
  <Kits>
    <Item>
      <kitName>65534_wsatlas_modkit</kitName>
      <id value="65534" />
      <kitType>MKT_SPECIAL</kitType>
      <visibleMods />
    </Item>
  </Kits>
</CVehicleModelInfoVarGlobal>`;

export const CLEAN_TEXT_ENTRIES = `AddTextEntry('WSATLAS', 'Workshop Atlas')
AddTextEntry('WORKSHOP', 'Workshop')
`;

// ─── Named fixtures ──────────────────────────────────────────────────────────

/**
 * The baseline. A correct add-on vehicle: manifest declares everything, every
 * name resolves, the mod kit id is allocated at the safe end of the range, and
 * the display name has a text entry behind it.
 *
 * No rule may fire on this at error severity. Ever.
 */
export function cleanVehicleResource(): GtaResourceInput {
  return resource(
    "wsatlas",
    [
      file("fxmanifest.lua", 400),
      file("vehicles.meta", 900),
      file("carvariations.meta", 400),
      file("carcols.meta", 300),
      file("handling.meta", 200),
      file("vehicle_names.lua", 100),
      file("stream/wsatlas.yft", 4_000_000),
      file("stream/wsatlas_hi.yft", 6_000_000),
      file("stream/wsatlas.ytd", 500_000),
      file("stream/wsatlas+hi.ytd", 900_000),
    ],
    {
      "fxmanifest.lua": CLEAN_MANIFEST,
      "vehicles.meta": CLEAN_VEHICLES,
      "carvariations.meta": CLEAN_CARVARIATIONS,
      "carcols.meta": CLEAN_CARCOLS,
      "handling.meta": CLEAN_HANDLING,
      "vehicle_names.lua": CLEAN_TEXT_ENTRIES,
    },
  );
}

/** One typo in `handlingId`, and everything downstream of it. */
export function vehicleMissingHandling(): GtaResourceInput {
  const input = cleanVehicleResource();
  input.texts.set(
    "vehicles.meta",
    CLEAN_VEHICLES.replace("<handlingId>wsatlas</handlingId>", "<handlingId>wsatls</handlingId>"),
  );
  return input;
}

/**
 * The mistake every generator used to make: kit id 0, which is stock GTA's
 * default kit and is therefore shared with most of the base-game fleet.
 */
export function vehicleStockModkitId(): GtaResourceInput {
  const input = cleanVehicleResource();
  input.texts.set("carcols.meta", CLEAN_CARCOLS.replace('value="65534"', 'value="0"'));
  return input;
}

/**
 * The same mistake on a police car, which is how it is usually discovered: not
 * by reading a report, but by every unit on the server losing their mods.
 */
export function vehicleEmergencyConflict(): GtaResourceInput {
  const input = cleanVehicleResource();
  const rename = (text: string) => text.replace(/wsatlas/g, "lspd_charger22");

  input.texts.set(
    "vehicles.meta",
    rename(CLEAN_VEHICLES).replace(
      "<vehicleClass>VC_SEDAN</vehicleClass>",
      "<vehicleClass>VC_EMERGENCY</vehicleClass>",
    ),
  );
  input.texts.set("carvariations.meta", rename(CLEAN_CARVARIATIONS));
  // Stock id 16 belongs to a kit the base-game police fleet uses, which is what
  // makes this the emergency case rather than an ordinary collision.
  input.texts.set("carcols.meta", rename(CLEAN_CARCOLS).replace('value="65534"', 'value="16"'));
  input.texts.set("handling.meta", rename(CLEAN_HANDLING));
  input.texts.set("vehicle_names.lua", rename(CLEAN_TEXT_ENTRIES.toUpperCase()));

  // The assets have to follow the rename, or the fixture tests "files missing"
  // rather than "kit collides", and the emergency claim gets lost in the noise.
  input.files = input.files.map((entry) =>
    entry.path.includes("wsatlas")
      ? { ...entry, path: rename(entry.path), name: rename(entry.name) }
      : entry,
  );
  return input;
}

/** A model name the game cannot hash: a space and a hyphen. */
export function vehicleInvalidSpawnName(): GtaResourceInput {
  const input = cleanVehicleResource();
  input.texts.set(
    "vehicles.meta",
    CLEAN_VEHICLES.replace("<modelName>wsatlas</modelName>", "<modelName>ws atlas-1</modelName>"),
  );
  return input;
}

/** A manifest and nothing else to argue with. */
export function manifestClean(): GtaResourceInput {
  return resource("clean", [file("fxmanifest.lua", 300)], { "fxmanifest.lua": CLEAN_MANIFEST });
}

/** `@ox_lib/init.lua` must resolve to a dependency, never to a missing file. */
export function manifestResourceInclude(): GtaResourceInput {
  return resource(
    "includes",
    [file("fxmanifest.lua", 300), file("server/main.lua", 200)],
    { "fxmanifest.lua": RESOURCE_INCLUDE_MANIFEST },
  );
}

/** The manifest says `Data/CarCols.meta`; the disk says `data/carcols.meta`. */
export function manifestCaseMismatch(): GtaResourceInput {
  return resource(
    "casing",
    [file("fxmanifest.lua", 200), file("data/carcols.meta", 300)],
    { "fxmanifest.lua": CASE_MISMATCH_MANIFEST },
  );
}

/**
 * A resource with another resource inside it. FiveM will not load the inner one
 * and the author usually has no idea, because the folder looks right.
 */
export function manifestNestedResource(): GtaResourceInput {
  return resource(
    "outer",
    [
      file("fxmanifest.lua", 200),
      file("inner/fxmanifest.lua", 200),
      file("inner/stream/inner.yft", 1000),
    ],
    {
      "fxmanifest.lua": CLEAN_MANIFEST,
      "inner/fxmanifest.lua": "fx_version 'cerulean'\ngame 'gta5'\n",
    },
  );
}

/** Two resources that need each other, plus one that provides an alias. */
export function dependenciesProvidesCycle(): GtaResourceInput[] {
  return [
    resource("alpha", [file("fxmanifest.lua")], {
      "fxmanifest.lua": `fx_version 'cerulean'\ngame 'gta5'\ndependencies { 'beta', 'chat' }\n`,
    }),
    resource("beta", [file("fxmanifest.lua")], {
      "fxmanifest.lua": `fx_version 'cerulean'\ngame 'gta5'\ndependencies { 'alpha' }\n`,
    }),
    resource("gamma", [file("fxmanifest.lua")], {
      "fxmanifest.lua": `fx_version 'cerulean'\ngame 'gta5'\nprovide 'es_extended'\n`,
    }),
    resource("delta", [file("fxmanifest.lua")], {
      "fxmanifest.lua": `fx_version 'cerulean'\ngame 'gta5'\ndependencies { 'es_extended', 'ox_lib', 'nonesuch' }\n`,
    }),
  ];
}

/** One resource replacing a base-game vehicle. Intentional, and not a conflict. */
export function streamVanillaOverride(): GtaResourceInput[] {
  return [
    resource("better-police", [file("fxmanifest.lua"), file("stream/police.yft", 5_000_000)], {
      "fxmanifest.lua": CLEAN_MANIFEST,
    }),
  ];
}

/** Two resources shipping one name. Exactly one of them wins, silently. */
export function streamCustomCollision(): GtaResourceInput[] {
  return [
    resource("pack-a", [file("fxmanifest.lua"), file("stream/police.yft", 5_000_000)], {
      "fxmanifest.lua": CLEAN_MANIFEST,
    }),
    resource("pack-b", [file("fxmanifest.lua"), file("stream/police.yft", 4_000_000)], {
      "fxmanifest.lua": CLEAN_MANIFEST,
    }),
  ];
}

/** A base-game name index small enough to read, for the override rules. */
export const VANILLA_SAMPLE = [
  "police.yft",
  "police.ytd",
  "police2.yft",
  "ambulance.yft",
  "firetruk.yft",
  "vehshare.ytd",
].join("\n");

// ─── Shared across briefs ────────────────────────────────────────────────────

/**
 * The fixtures more than one brief needs, built once.
 *
 * What is left here is the `GtaResourceInput` half: the collision pair, the
 * wrong-order resource set, the shared-handling packs. They are inputs the
 * *analyzer* takes, and what each one is about is a judgement the analyzer
 * makes rather than a gesture the browser makes, so they carry declared sizes
 * and no bytes.
 *
 * Their `path → content` counterparts — the pack maps a browser can actually
 * materialise as files — moved to `demoPacks.ts` when the free tools started
 * offering them as demos, and are re-exported at the top of this file so every
 * caller here keeps its import. The split is not cosmetic: a `PackFiles` map
 * can be zipped, picked or dragged and compared three ways, and a
 * `GtaResourceInput` is already past intake and cannot be any of those. That is
 * also why the collision pair below is not a demo pack — see the argument in
 * `demoPacks.ts` about which shape can be demo input.
 */

const SHARED_PART_BYTES = 2_400_000;

/** The far copy in `collisionDifferent`: a different length, so different bytes. */
const SHADOWING_PART_BYTES = 3_100_000;

/**
 * Two parts packs from one vendor, both streaming `COLLISION_ASSET`.
 *
 * Everything except the size of the far copy is fixed here so the two exported
 * fixtures differ in one fact and nothing else. Each pack also ships a model of
 * its own, so a test can tell "the one name that collides" from "every name in
 * the pack" — and so a duplicate-finder has something it must *not* flag.
 *
 * The names are stable and distinct on purpose: Clash Finder's "which copy wins"
 * test writes a `server.cfg` that `ensure`s these two in a chosen order.
 */
function collisionPair(farCopyBytes: number): [GtaResourceInput, GtaResourceInput] {
  return [
    resource(
      "wheels-pack-a",
      [
        file("fxmanifest.lua", 300),
        file(`stream/${COLLISION_ASSET}`, SHARED_PART_BYTES),
        file("stream/wsatlas.yft", 4_000_000),
      ],
      { "fxmanifest.lua": STREAM_ONLY_MANIFEST },
    ),
    resource(
      "wheels-pack-b",
      [
        file("fxmanifest.lua", 300),
        file(`stream/${COLLISION_ASSET}`, farCopyBytes),
        file("stream/wsbravo.yft", 3_500_000),
      ],
      { "fxmanifest.lua": STREAM_ONLY_MANIFEST },
    ),
  ];
}

/**
 * `collision-identical/` — one streamed name, two resources, the same bytes.
 *
 * Serves Clash Finder ("identical, harmless", not a red finding) and Stream
 * Budget (`budget.duplicate`: one file downloaded and paid for twice).
 *
 * `stream.basename.duplicate` fires on this pair, and should: the namespace is
 * flat, so one of the two copies still loses. But losing to a byte-for-byte copy
 * of itself changes nothing in game — the player sees the same wheel either way
 * — and painting that the same red as a genuine shadowing is how a reader learns
 * to skip the red. What it costs is bandwidth and streaming memory, which is
 * Stream Budget's finding, not a correctness one.
 *
 * How the claim is made, and its limits. `identicalPairs` is the caller telling
 * the analyzer the answer to a comparison the caller paid for; the analyzer
 * never sees the bytes, because two 20 MB models is not a read to make on the
 * analyzer's behalf. Its paths are relative to a resource, so the far side of a
 * cross-resource claim has to be qualified with the resource name — that
 * spelling exists nowhere else and a consumer must not assume it of the field in
 * general. It is recorded once, on the first half, because it is one fact about
 * a pair and a consumer that walks every resource would otherwise report it
 * twice.
 */
export function collisionIdentical(): GtaResourceInput[] {
  const [a, b] = collisionPair(SHARED_PART_BYTES);
  a.identicalPairs = [[`stream/${COLLISION_ASSET}`, `${b.name}/stream/${COLLISION_ASSET}`]];
  return [a, b];
}

/**
 * `collision-different/` — the same pair, with one thing changed: the two copies
 * of `COLLISION_ASSET` are not the same file.
 *
 * Serves Clash Finder ("different, one is being shadowed" — the half of the
 * split that is worth acting on) and Stream Budget (a name shared by two
 * resources that is not a duplicate to be deleted).
 *
 * The missing `identicalPairs` claim is not what makes this the different half.
 * An absent claim means nobody compared the two, which is not evidence either
 * way, and a fixture that leaned on it would be teaching the reader the wrong
 * rule. What makes it different is the size: two files of different lengths
 * cannot be the same bytes. That asymmetry is the reason the field exists —
 * sizes alone can prove *different* and never prove *identical*, so the identical
 * half has to be told and this half does not.
 */
export function collisionDifferent(): GtaResourceInput[] {
  return collisionPair(SHADOWING_PART_BYTES);
}

// ─── Spawn Index ─────────────────────────────────────────────────────────────

/**
 * The fixtures the spawn-code reader is argued against.
 *
 * All but two of them are the `20ambocr/` resource at the repo root with exactly
 * one thing changed, and that resource is a real pack somebody published rather
 * than an invention: a legacy `__resource.lua`, a `vehicles.meta` whose
 * `gameName` is a *label key* and not a name, a make of `SPACE` registered under
 * a hex hash, and a `stream/` folder holding one vehicle and thirty mod shop
 * parts. Every one of those is a case a synthetic fixture would have left out,
 * and each is the thing one of the rules below exists for.
 *
 * They are typed out here rather than read off disk for the reason
 * `__fixtures__/handlingMeta.ts` gives about the one fixture that *is* read off
 * disk: a test in this layer that resolves a path against `process.cwd()`
 * depends on where vitest was started and on a folder that is not this layer's
 * to keep. What is reproduced is every field these rules read; the eighty lines
 * of camera offsets in the original are not.
 */

/** `20ambocr`'s manifest: the pre-2019 form, with every meta registered. */
export const AMBO_MANIFEST = `resource_manifest_version '77731fab-63ca-442c-a67b-abc70f28dfa5'

files {
    'vehiclelayouts.meta',
    'vehicles.meta',
    'carvariations.meta',
    'carcols.meta',
    'handling.meta',
}

data_file 'VEHICLE_LAYOUTS_FILE' 'vehiclelayouts.meta'
data_file 'HANDLING_FILE' 'handling.meta'
data_file 'VEHICLE_METADATA_FILE' 'vehicles.meta'
data_file 'CARCOLS_FILE' 'carcols.meta'
data_file 'VEHICLE_VARIATION_FILE' 'carvariations.meta'

client_script {
    'vehicle_names.lua'
}
`;

/**
 * The real `vehicles.meta`, with the camera and IK offsets dropped.
 *
 * `gameName` and `vehicleMakeName` are kept exactly as shipped, because they are
 * the point: both are text-label keys, neither is a display name, and the export
 * that matters is the one that resolves them through `vehicle_names.lua` instead
 * of printing `20ambocrew` into a dealership menu.
 */
export const AMBO_VEHICLES = `<?xml version="1.0" encoding="UTF-8"?>
<!-- 2020 Ford 20ambocr - GeorgieMoon -->
<CVehicleModelInfo__InitDataList>
  <residentTxd>vehshare</residentTxd>
  <residentAnims />
  <InitDatas>
    <Item>
      <modelName>20ambocr</modelName>
      <txdName>20ambocr</txdName>
      <handlingId>20ambocr</handlingId>
      <gameName>20ambocrew</gameName>
      <vehicleMakeName>SPACE</vehicleMakeName>
      <audioNameHash>fbi2</audioNameHash>
      <layout>LAYOUT_S101AMB</layout>
      <flags>FLAG_EXTRAS_STRONG FLAG_HAS_LIVERY</flags>
      <vehicleClass>VC_EMERGENCY</vehicleClass>
    </Item>
  </InitDatas>
</CVehicleModelInfo__InitDataList>
`;

export const AMBO_HANDLING = `<?xml version="1.0" encoding="UTF-8"?>
<CHandlingDataMgr>
  <HandlingData>
    <Item type="CHandlingData">
      <handlingName>20ambocr</handlingName>
    </Item>
  </HandlingData>
</CHandlingDataMgr>
`;

export const AMBO_CARVARIATIONS = `<?xml version="1.0" encoding="UTF-8"?>
<CVehicleModelInfoVariation>
  <variationData>
    <Item>
      <modelName>20ambocr</modelName>
      <kits>
        <Item>4826_20ambocr_modkit</Item>
      </kits>
      <sirenSettings value="0" />
      <lightSettings value="0" />
    </Item>
  </variationData>
</CVehicleModelInfoVariation>
`;

export const AMBO_CARCOLS = `<?xml version="1.0" encoding="UTF-8"?>
<CVehicleModelInfoVarGlobal>
  <Kits>
    <Item>
      <kitName>4826_20ambocr_modkit</kitName>
      <id value="4826" />
      <kitType>MKT_STANDARD</kitType>
      <visibleMods />
    </Item>
  </Kits>
</CVehicleModelInfoVarGlobal>
`;

export const AMBO_LAYOUTS = `<?xml version="1.0" encoding="UTF-8"?>
<CVehicleMetadataMgr>
  <VehicleLayoutInfos>
    <Item type="CVehicleLayoutInfo">
      <Name>LAYOUT_S101AMB</Name>
    </Item>
  </VehicleLayoutInfos>
</CVehicleMetadataMgr>
`;

/**
 * The display names, registered the way the pack really registers them: by hash,
 * and one of the two under the wrong hash.
 *
 * `0x488AD9F6` is the joaat of `SPACE`, which is what `vehicleMakeName` says, so
 * the make resolves. `0x01F9FB91` is the joaat of `20ambocr` — the *model* name
 * — while `gameName` is `20ambocrew`, so the display name resolves to nothing
 * and a player sees the raw text `20ambocrew` in the menus. That is not an error
 * in transcription: it is what the published pack ships, it is one of the most
 * common mistakes in the hobby, and a fixture that quietly corrected it would
 * remove the case the export rule exists for.
 *
 * The hex form is kept for the same reason. A reader that only understands
 * `AddTextEntry('SPACE', …)` finds neither entry and reports a correct make as
 * unresolved, which is why `textEntryHash` accepts both forms.
 */
export const AMBO_TEXT_ENTRIES = `Citizen.CreateThread(function()
    AddTextEntry('0x488AD9F6', 'SPACE')
    AddTextEntry('0x01F9FB91', '2020 F450 Ambulance')
end)
`;

/** The mod shop parts `20ambocr` ships beside its one vehicle, as it really ships them. */
const AMBO_PART_MODELS: readonly string[] = [
  "bbr_1",
  "bbr_2",
  "bbr_3",
  "bdr_1",
  "bg_1",
  "chr_1",
  "grl_1",
  "hrn_1",
  "ten_1",
  "trd_1",
];

/**
 * `20ambocr/` — one vehicle, one spawn code, and ten models that are not one.
 *
 * The baseline for the spawn reader the way `cleanVehicleResource` is the
 * baseline for the analyzer: one code comes back, it comes back from the meta
 * tier, and every part file beside it stays visible with a reason rather than
 * being deleted by a blacklist.
 */
export function ambulanceResource(): GtaResourceInput {
  return resource(
    "20ambocr",
    [
      file("__resource.lua", 500),
      file("vehicles.meta", 9_000),
      file("carvariations.meta", 3_000),
      file("carcols.meta", 12_000),
      file("handling.meta", 4_000),
      file("vehiclelayouts.meta", 8_000),
      file("vehicle_names.lua", 150),
      file("stream/20ambocr.yft", 5_500_000),
      file("stream/20ambocr_hi.yft", 7_200_000),
      file("stream/20ambocr.ytd", 4_100_000),
      file("stream/20ambocr+hi.ytd", 8_300_000),
      file("stream/va_20ambo.ycd", 60_000),
      ...AMBO_PART_MODELS.map((name) => file(`stream/${name}.yft`, 300_000)),
    ],
    {
      "__resource.lua": AMBO_MANIFEST,
      "vehicles.meta": AMBO_VEHICLES,
      "carvariations.meta": AMBO_CARVARIATIONS,
      "carcols.meta": AMBO_CARCOLS,
      "handling.meta": AMBO_HANDLING,
      "vehiclelayouts.meta": AMBO_LAYOUTS,
      "vehicle_names.lua": AMBO_TEXT_ENTRIES,
    },
  );
}

/**
 * The same pack with the one line that registers `vehicles.meta` removed.
 *
 * Nothing else changes, which is what makes it the fixture: the file is on disk,
 * it parses, it declares a spawn code, and the game never reads any of it. A
 * tool that trusts a meta because it exists reports a dead name as a live spawn
 * code, and the user types it into a console and gets nothing back.
 */
export function ambulanceMetaUnregistered(): GtaResourceInput {
  const input = ambulanceResource();
  input.texts.set(
    "__resource.lua",
    AMBO_MANIFEST.replace("data_file 'VEHICLE_METADATA_FILE' 'vehicles.meta'\n", ""),
  );
  return input;
}

/**
 * The same pack with its `vehicles.meta` cut off mid-tag.
 *
 * A download interrupted at 99%, or an editor that saved while the file was
 * still being written. It is the most confusing failure this tool has — the pack
 * visibly ships a `vehicles.meta` and the report shows guesses — so the parser's
 * own complaint has to reach the user rather than being computed, truncated and
 * dropped.
 */
export function ambulanceMetaMalformed(): GtaResourceInput {
  const input = ambulanceResource();
  input.texts.set("vehicles.meta", AMBO_VEHICLES.replace("</InitDatas>", "<Item><modelName>"));
  return input;
}

/**
 * The same pack with the base model gone and the high-detail one left behind.
 *
 * The shape of a repack that dropped a file: `vehicles.meta` still declares the
 * code, `20ambocr_hi.yft` is still there, and the vehicle has no model at all,
 * because the high-detail model is loaded on top of the base one rather than
 * instead of it. "3 codes in vehicles.meta have no model file" is the sentence
 * this exists to produce.
 */
export function ambulanceModelMissing(): GtaResourceInput {
  const input = ambulanceResource();
  input.files = input.files.filter((entry) => entry.path !== "stream/20ambocr.yft");
  return input;
}

/**
 * The same pack with its real siren and light ids, for `carcols.ts`.
 *
 * `ambulanceResource()` zeroes out `sirenSettings`/`lightSettings` because the
 * spawn-index rules it was built for do not care what they hold. The published
 * pack does not ship zero: it ships `sirenSettings 289563` and
 * `lightSettings 684`, both defined right there in its own `carcols.meta` and
 * both wildly out of range — a `lightSettings` field is one byte, and 289563
 * does not fit in a `sirenSettings` field even with the Siren Setting Limit
 * Adjuster loaded. `id-bench.md`'s own case for why this tool needs a real
 * range check *is* this pack, so this is the one fixture in the set that keeps
 * the real numbers instead of the placeholder.
 */
export function ambulanceOutOfRangeCarcols(): GtaResourceInput {
  const input = ambulanceResource();
  input.texts.set(
    "carvariations.meta",
    AMBO_CARVARIATIONS.replace('<sirenSettings value="0" />', '<sirenSettings value="289563" />').replace(
      '<lightSettings value="0" />',
      '<lightSettings value="684" />',
    ),
  );
  input.texts.set(
    "carcols.meta",
    AMBO_CARCOLS.replace(
      "  </Kits>\n</CVehicleModelInfoVarGlobal>",
      `  </Kits>
  <Sirens>
    <Item>
      <id value="289563" />
      <name>20ambocr</name>
    </Item>
  </Sirens>
  <Lights>
    <Item>
      <id value="684" />
    </Item>
  </Lights>
</CVehicleModelInfoVarGlobal>`,
    ),
  );
  return input;
}

/**
 * A stream-only parts pack holding the four names a substring blacklist eats.
 *
 * `gtr_model`, `police_extra`, `sultan_roof` and `x_grilles` are the documented
 * casualties of the prior art's filter: each contains a mod-part word, each is a
 * plausible vehicle, and all four vanished with no way to see that they had.
 * Here they come back demoted, carrying the sentence that demoted them, which a
 * user can read and overrule.
 *
 * `wsbravo` ships with a texture dictionary and is what an unambiguous vehicle
 * looks like, so a test can tell "ranked low" apart from "not found".
 */
export function partsPackBlacklistVictims(): GtaResourceInput {
  return resource(
    "blacklist-bait",
    [
      file("fxmanifest.lua", 200),
      file("stream/gtr_model.yft", 3_000_000),
      file("stream/police_extra.yft", 2_800_000),
      file("stream/sultan_roof.yft", 2_600_000),
      file("stream/x_grilles.yft", 1_200_000),
      file("stream/wsbravo.yft", 4_000_000),
      file("stream/wsbravo.ytd", 900_000),
    ],
    { "fxmanifest.lua": STREAM_ONLY_MANIFEST },
  );
}

/**
 * Two packs from two vendors that both declare the same spawn code.
 *
 * *The* classic FiveM breakage: both resources start, the game keeps one model
 * per name, and which one the server gets depends on load order — so it changes
 * the day an unrelated resource is added. Neither pack is wrong on its own,
 * which is why only a view across the set can report it.
 *
 * Distinct from `twoPacksSameLayout`, which is the same shape one layer down:
 * there the two packs share their *file paths* and their spawn codes differ, so
 * it tests that intake keeps two packs apart. Here the paths differ and the
 * codes collide.
 */
export function twoPacksSameSpawnCode(code = "police"): GtaResourceInput[] {
  const pack = (origin: string): GtaResourceInput => ({
    ...resource(
      "vehicle-pack",
      [
        file("fxmanifest.lua", 300),
        file("vehicles.meta", 600),
        file(`stream/${code}.yft`, 4_000_000),
        file(`stream/${code}.ytd`, 900_000),
      ],
      { "fxmanifest.lua": CLEAN_MANIFEST, "vehicles.meta": vehiclesMeta(code) },
    ),
    identity: `${origin}/vehicle-pack`,
    origin,
  });
  return [pack("vendor-a.zip"), pack("vendor-b.zip")];
}

// ─── server.cfg ──────────────────────────────────────────────────────────────

/**
 * `cfg-wrong-order/` — four resources whose folder order is the order that does
 * not boot.
 *
 * This is the competitor's headline defect made concrete. Its generator's
 * registry line is "A working server.cfg, in the right order" and it emits these
 * four in whatever order it was handed them: `ox_inventory` calls into `ox_lib`
 * before `ox_lib` exists, `es_extended` queries a database through `oxmysql`
 * that has not started, and the server comes up with a wall of nil exports that
 * goes away on the next restart — which is why the fault survives testing.
 *
 * The dependencies are declared rather than inferred on purpose. An inferred
 * edge is a guess read out of script text, and this layer refuses to raise an
 * error on a guess; a fixture built on inference would be testing the weaker
 * half of the rule and would pass if the stronger half broke.
 *
 * Serves CFG Builder (`cfg.ensure.order`, and the reordered ensure block) and
 * Clash Finder, which needs a folder whose declared order and whose correct
 * order are different so "which copy wins today" is not the same answer as
 * "which copy should win".
 */
export function cfgWrongOrder(): GtaResourceInput[] {
  const manifest = (...needs: string[]): string =>
    `fx_version 'cerulean'\ngame 'gta5'\n` +
    (needs.length === 0 ? "" : `dependencies { ${needs.map((n) => `'${n}'`).join(", ")} }\n`);

  // Folder order is deliberately the broken one: every dependent comes first.
  return [
    resource("ox_inventory", [file("fxmanifest.lua")], {
      "fxmanifest.lua": manifest("ox_lib", "oxmysql"),
    }),
    resource("es_extended", [file("fxmanifest.lua")], {
      "fxmanifest.lua": manifest("oxmysql"),
    }),
    resource("ox_lib", [file("fxmanifest.lua")], { "fxmanifest.lua": manifest() }),
    resource("oxmysql", [file("fxmanifest.lua")], { "fxmanifest.lua": manifest() }),
  ];
}

/**
 * A hostname carrying every character that breaks a naive generator.
 *
 * A trailing backslash (escapes its own closing quote, so the rest of the file
 * becomes one string), a `#` (starts a comment outside quotes), a double quote,
 * and a newline (splits one directive in two, and the server tries to run the
 * second half as a command).
 *
 * The value ends in a single backslash. That is the one that matters and it is
 * easy to lose while editing this line: in the source it is written `\\\\`, and
 * a test asserts the string's last character is a backslash so a future edit
 * cannot quietly remove the case.
 *
 * Serves CFG Builder. Any tool that writes a value into a config file wants it.
 */
export const HOSTILE_HOSTNAME = 'Bob\'s #1 "Best" Server\nnow hosted at C:\\servers\\';

// ─── Clash Finder ────────────────────────────────────────────────────────────

/**
 * A `handling.meta` with one entry, named.
 *
 * Parameterised rather than fixed because the pair below has to exist twice —
 * once where the two entries collide and once where they do not — and a rule
 * that fires on the fact that two packs both ship a `handling.meta` would be
 * wrong about every multi-pack server on earth. The negative half is the half
 * that proves the rule is the right one.
 */
export function handlingMeta(handlingName: string): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<CHandlingDataMgr>
  <HandlingData>
    <Item type="CHandlingData">
      <handlingName>${handlingName}</handlingName>
    </Item>
  </HandlingData>
</CHandlingDataMgr>
`;
}

/** A manifest that registers one `handling.meta` and nothing else. */
const HANDLING_ONLY_MANIFEST = `fx_version 'cerulean'
game 'gta5'

files {
  'handling.meta',
}

data_file 'HANDLING_FILE' 'handling.meta'
`;

/**
 * Two vehicle packs that both register `HANDLING_FILE handling.meta`.
 *
 * The declaration is identical in both and that on its own is not a fault: it is
 * what every vehicle pack writes, and a checker that reported it would produce
 * one red finding naming forty correct resources on the first real server it
 * saw. What decides whether this is a clash is the *contents* — the two files
 * are merged into one server-wide handling table, so two entries under one
 * `handlingName` land on each other and one of them never applies. The vehicle
 * then drives with the other pack's physics, and nothing in the console says so.
 *
 * `handlingName` is the parameter for exactly that reason. Called with one name
 * this is the breakage; called with two it is the control that must stay silent.
 * Invisible to a filename-only check either way: both files are called
 * `handling.meta` and both are supposed to be.
 *
 * Serves Clash Finder (`manifest.data_file.double_registered`).
 */
export function dataFileSharedHandling(
  first = "police",
  second = first,
): GtaResourceInput[] {
  const pack = (name: string, entry: string): GtaResourceInput =>
    resource(name, [file("fxmanifest.lua", 200), file("handling.meta", 900)], {
      "fxmanifest.lua": HANDLING_ONLY_MANIFEST,
      "handling.meta": handlingMeta(entry),
    });
  return [pack("lspd-pack", first), pack("bcso-pack", second)];
}

/**
 * Two packs that both register the same audio bank under `AUDIO_GAMEDATA`.
 *
 * The other half of the `data_file` namespace, and the half no comparison of
 * file names can reach. `audioconfig/dlcambo_game` is not a path into the
 * resource that happens to repeat — it is the name the whole server files that
 * audio data under, and the file behind it is called `dlcambo_game.dat151.rel`,
 * so the declared value and the file name share nothing. Two resources claiming
 * the name means one registration lands on the other.
 *
 * Both packs also carry the `manifest.path.missing` that a real audio pack
 * carries here, because the declared value is a stem with no file of that exact
 * name on disk. That is the analyzer being right about a real quirk of audio
 * declarations, not a flaw in the fixture, and a test that asserted a clean
 * report over this pair would be asserting the wrong thing.
 *
 * Serves Clash Finder (`manifest.data_file.double_registered`, the global-name
 * half).
 */
export function dataFileSharedAudioBank(): GtaResourceInput[] {
  const manifest = `fx_version 'cerulean'
game 'gta5'

files {
  'audioconfig/dlcambo_game.dat151.rel',
}

data_file 'AUDIO_GAMEDATA' 'audioconfig/dlcambo_game'
`;
  const pack = (name: string): GtaResourceInput =>
    resource(
      name,
      [
        file("fxmanifest.lua", 200),
        file("audioconfig/dlcambo_game.dat151.rel", 40_000),
      ],
      { "fxmanifest.lua": manifest },
    );
  return [pack("ambo-sounds-a"), pack("ambo-sounds-b")];
}

/**
 * Two interiors that each ship `data/audio_occlusion.dat` and each declare it.
 *
 * The control for the *global-name* half of the `data_file` rule, and the exact
 * counterpart of `dataFileSharedHandling`'s negative case one namespace over.
 * `AUDIO_WAVEPACK` is a global-name type and this declaration is correct in both
 * packs: `audio_occlusion.dat` is the name FiveM's own filename-to-type table
 * fixes for occlusion data, so every MLO that ships it ships it under that name,
 * in that folder, under that type. Two of them sharing the string is two packs
 * following one convention, not two packs claiming one server-global name.
 *
 * Keyed on the basename alone — which is what the rule did before this fixture
 * existed — it produced an error-severity "rename the data in one of them" over
 * two correct interiors, which is the forty-correct-resources failure the rule's
 * own comment is written against, reached through the audio door instead of the
 * handling one.
 *
 * Serves Clash Finder (the global-name rule must stay silent here).
 */
export function dataFileFixedConventionName(): GtaResourceInput[] {
  const manifest = `fx_version 'cerulean'
game 'gta5'

files {
  'data/audio_occlusion.dat',
}

data_file 'AUDIO_WAVEPACK' 'data/audio_occlusion.dat'
`;
  const pack = (name: string): GtaResourceInput =>
    resource(name, [file("fxmanifest.lua", 200), file("data/audio_occlusion.dat", 5_000)], {
      "fxmanifest.lua": manifest,
    });
  return [pack("mlo-bank-heist"), pack("mlo-police-mrpd")];
}

/** A `carvariations.meta` with one entry, for the model named. */
export function carvariationsMeta(model: string): string {
  return `<?xml version="1.0" encoding="UTF-8"?>
<CVehicleModelInfoVariation>
  <variationData>
    <Item>
      <modelName>${model}</modelName>
    </Item>
  </variationData>
</CVehicleModelInfoVariation>
`;
}

/**
 * Two ordinary vehicle packs that define one model in every file that mentions
 * it — the most common real conflict on a FiveM server.
 *
 * `CLEAN_MANIFEST` registers the usual four metas, so one duplicated model name
 * reaches four different rules at once: the archetype registration, the merged
 * handling table, the merged carvariations table and the streamed `.yft`. That
 * is what makes this the fixture for the question "how many collisions is one
 * duplicated spawn code" — a question `twoPacksSameSpawnCode` cannot ask,
 * because its packs carry no meta beyond `vehicles.meta`.
 *
 * The modkit is named per pack on purpose. Two packs sharing one `kitName` is a
 * real and separate clash, and leaving it in would make every count below depend
 * on a fault that has nothing to do with the spawn code.
 *
 * Serves Clash Finder (one contested name, one card per registration that has to
 * change, and never two cards for one registration).
 */
export function twoPacksSameVehicleEverywhere(model = "police"): GtaResourceInput[] {
  const carcols = (kit: string) => `<?xml version="1.0" encoding="UTF-8"?>
<CVehicleModelInfoVarGlobal>
  <Kits>
    <Item>
      <kitName>${kit}</kitName>
    </Item>
  </Kits>
</CVehicleModelInfoVarGlobal>
`;
  const pack = (name: string): GtaResourceInput =>
    resource(
      name,
      [
        file("fxmanifest.lua", 300),
        file("vehicles.meta", 900),
        file("carvariations.meta", 500),
        file("carcols.meta", 400),
        file("handling.meta", 600),
        file(`stream/${model}.yft`, 4_000_000),
      ],
      {
        "fxmanifest.lua": CLEAN_MANIFEST,
        "vehicles.meta": vehiclesMeta(model),
        "carvariations.meta": carvariationsMeta(model),
        "carcols.meta": carcols(`${name}_modkit`),
        "handling.meta": handlingMeta(model),
      },
    );
  return [pack("pack-a"), pack("pack-b")];
}

/**
 * A `vehicles.meta` whose only `<modelName>` sits inside an XML comment.
 *
 * The single case that separates a parser from a scraper. A regex over the raw
 * text finds `wsatlas` twice here and reports a spawn code the file does not
 * declare; a DOM has one element and one comment node, and a comment is not an
 * element. The live entry is named differently on purpose, so a test can assert
 * both halves at once — the commented name absent, the real name present —
 * rather than only that a count came out low.
 *
 * Serves Clash Finder (`<modelName>` inside an XML comment is not counted).
 */
export const COMMENTED_MODEL_VEHICLES = `<?xml version="1.0" encoding="UTF-8"?>
<CVehicleModelInfo__InitDataList>
  <InitDatas>
    <!--
    <Item>
      <modelName>wscommented</modelName>
      <txdName>wscommented</txdName>
      <handlingId>wscommented</handlingId>
    </Item>
    -->
    <Item>
      <modelName>wslive</modelName>
      <txdName>wslive</txdName>
      <handlingId>wslive</handlingId>
    </Item>
  </InitDatas>
</CVehicleModelInfo__InitDataList>
`;

// ─── Stream Budget ───────────────────────────────────────────────────────────

/**
 * The two drops the aggregate budget rules are argued against.
 *
 * These are `ResourceDrop`s and not `GtaResourceInput`s, because the budget
 * analyzer reads `allPaths` — intake's unfiltered inventory — and never the
 * analysable subset. That is the whole point of the module: a tool that sizes a
 * folder from the filtered list reports a clean 390 MB for a 590 MB download.
 *
 * They are also sizes with no bytes behind them, which is the one thing here
 * that would be wrong to copy elsewhere. Materialising fifty real 15 MB files to
 * prove a rule about 750 MB would cost three quarters of a gigabyte of test
 * memory to learn nothing: the rule is arithmetic over `allPaths`, and
 * `analyzeBudget` is documented to read exactly two things from a drop — every
 * path's size, and every resource's `root`. `files` is therefore empty on
 * purpose, and a test that starts needing it is a test whose subject has moved.
 */
export function budgetDrop(
  packs: { name: string; root?: string; origin?: string; files: [path: string, size: number][] }[],
): ResourceDrop {
  const allPaths: { path: string; size: number }[] = [];
  const origins = new Map<string, string>();
  const resources = packs.map((pack) => {
    const root = pack.root ?? pack.name;
    for (const [path, size] of pack.files) allPaths.push({ path: `${root}/${path}`, size });
    if (pack.origin !== undefined) origins.set(root, pack.origin);
    return { name: pack.name, root, files: [] };
  });
  return { resources, allPaths, origins, truncated: [] };
}

/** The size the aggregate rules are calibrated against: big, and legal per file. */
const DICTIONARY_BYTES = 15 * 1024 * 1024;

/**
 * Fifty 15 MB texture dictionaries in one resource.
 *
 * Not one of them is over the 16 MB per-file ceiling, so every per-file rule in
 * the tool this replaces passes it, and the resource is 750 MB — the most common
 * real bloat pattern on a busy server, and the one their five per-file rules are
 * structurally unable to see.
 *
 * Serves Stream Budget (`budget.resource.total`).
 */
export function fiftyTextureDictionaries(): ResourceDrop {
  return budgetDrop([
    {
      name: "liveries",
      origin: "liveries",
      files: [
        ["fxmanifest.lua", 300],
        ...Array.from(
          { length: 50 },
          (_unused, index): [string, number] => [
            `stream/livery_${String(index + 1).padStart(2, "0")}.ytd`,
            DICTIONARY_BYTES,
          ],
        ),
      ],
    },
  ]);
}

/** The wheel dictionary six vehicle packs each ship their own copy of. */
export const SHARED_DICTIONARY = "ws_wheel_hre.ytd";

const SHARED_DICTIONARY_BYTES = 12 * 1024 * 1024;

/**
 * Six vehicle packs, each shipping its own copy of one 12 MB dictionary.
 *
 * 72 MB downloaded to deliver 12 MB, which the research calls the highest-value
 * finding on a real server — and the tool this replaces has every path and every
 * size in hand and says nothing about it.
 *
 * Each pack also ships a model of its own at a size no other pack repeats, so a
 * duplicate finder has something it must *not* flag, and the six copies are the
 * only files in the drop that share a size.
 *
 * Serves Stream Budget (`budget.duplicate`).
 */
export function sharedDictionaryAcrossPacks(): ResourceDrop {
  return budgetDrop(
    Array.from({ length: 6 }, (_unused, index) => ({
      name: `vehicle-pack-${index + 1}`,
      origin: `vendor-${index + 1}.zip`,
      files: [
        ["fxmanifest.lua", 300] as [string, number],
        [`stream/${SHARED_DICTIONARY}`, SHARED_DICTIONARY_BYTES] as [string, number],
        [`stream/car_${index + 1}.yft`, 3_000_000 + index * 1_000] as [string, number],
      ],
    })),
  );
}
